/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import isNil from 'lodash.isnil';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {SensorTypes, SmarterTrailerIssueTypes} from '@ion/core/src/types/entities';
import {dateFormatter, timeFormatter} from '@ion/core/src/utils/DateUtil';
import {TireIcon} from '../../icons/TireIcon';
import {SmarterTrailerWidgetInfo} from '../../types/types';
import {getTimeDifference, getTirePressureLabels} from './helper';
import WidgetContainer from './WidgetContainer';
import WidgetDescription from './WidgetDescription';

export type TirePressureProps = {
  tirePressure: SmarterTrailerWidgetInfo;
  sensorType?: SensorTypes;
};

/**
 * Defines Tire Pressure component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 * import React from 'react';
 * import {TirePressureWidget} from '@ion/dls/src/components/smarterTrailer';
 *
 * <TirePressureWidget tirePressure={tirePressure} />
 *
 * ```
 */
export const TirePressureWidget = ({tirePressure}: TirePressureProps) => {
  // localization
  const {
    label,
    highPressure,
    normalPressure,
    lowPressure
  } = getTirePressureLabels();

  /**
   * Define a utility to get the Tire Pressure Value
   */
  const getTirePressureValue = ({issues}: SmarterTrailerWidgetInfo): string => {
    const high = issues[SmarterTrailerIssueTypes.highPressure] || issues[SmarterTrailerIssueTypes.highPressureAlarm];
    const low = issues[SmarterTrailerIssueTypes.lowPressure] || issues[SmarterTrailerIssueTypes.lowPressureAlarm];
    if ((high && low) || low) {
      return lowPressure;
    } else if (high) {
      return highPressure;
    }
    return normalPressure;
  };

  if (tirePressure) {
    const {messageTime: timeStamp} = tirePressure;
    const today       = getTimeDifference(timeStamp);
    const messageDate = dateFormatter(timeStamp, UserPreferences.getSystemDateFormat());
    const messageTime = isNil(today) ? timeFormatter(timeStamp, UserPreferences.getSystemTimeFormat()) : today;
    const value       = getTirePressureValue(tirePressure);
    const style       = value === highPressure || value === lowPressure ? {color: 'red'} : {};
    return (
      <WidgetContainer>
        <TireIcon />
        <WidgetDescription label={label}
          value={value}
          messageTime={messageTime}
          messageDate={messageDate}
          valueStyle={style} />
      </WidgetContainer>
    );
  }
  return null;
};

export default TirePressureWidget;
